import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
    BookOpen, 
    Download, 
    Sparkles, 
    Mail, 
    ArrowRight, 
    Calendar,
    Loader2, 
    CheckCircle2, 
    AlertCircle, 
    FileText,
    Smile
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getAttributionPayload, initAttribution } from '@/lib/attribution';

const getApiUrl = () => {
    if (typeof window === 'undefined') return 'http://localhost:8001/api';
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'http://localhost:8001/api';
    }
    return `${window.location.origin}/api`;
};
const API = getApiUrl();

const FALLBACK_NEWS_ISSUES = [
    {
        id: "magazine-news-issue-01",
        issue: 1,
        type: "news",
        title: "The Neural Feed Weekly — Issue #01",
        period_label: "19–25 October 2026",
        page_count: 10,
        description: "OpenAI chases $30B at a $1.25T valuation, Anthropic launches Claude Duet across apps, and TorchNeuron chips.",
        cover_image: "/magazine_previews/news/cover_v01.webp",
        pdf_filename: "The_Neural_Feed_Weekly_Issue_01.pdf"
    }
];

export default function PastIssuesPage() {
    const navigate = useNavigate();
    const [newsIssues, setNewsIssues] = useState(FALLBACK_NEWS_ISSUES);
    const [toonIssues, setToonIssues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [subStatus, setSubStatus] = useState('idle');
    const [subMessage, setSubMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [subscriberToken] = useState(() => localStorage.getItem('tnf_subscriber_token') || '');
    const [isSubscribed, setIsSubscribed] = useState(() => Boolean(localStorage.getItem('tnf_subscribed')) || Boolean(localStorage.getItem('tnf_subscriber_token')));

    useEffect(() => {
        initAttribution();
        document.title = "The Neural Feed Weekly | Past Issues & Archive";
        window.scrollTo(0, 0);

        setLoading(true);
        // 1. Fetch news issues as primary
        axios.get(`${API}/magazines?type=news&limit=20`)
            .then(res => {
                if (Array.isArray(res.data?.magazines) && res.data.magazines.length > 0) {
                    setNewsIssues(res.data.magazines);
                }
            })
            .catch(err => {
                console.log('Using default news issues list:', err);
            });

        // 2. Fetch toon issues for the comics archive section
        axios.get(`${API}/magazines?type=toons&limit=20`)
            .then(res => {
                if (Array.isArray(res.data?.magazines) && res.data.magazines.length > 0) {
                    setToonIssues(res.data.magazines);
                }
            })
            .catch(err => {
                console.log('Could not load toon archive:', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const handleSubscribe = async (e) => {
        e.preventDefault();
        const cleanEmail = (email || '').trim().toLowerCase();
        if (!cleanEmail || !cleanEmail.includes('@')) {
            setSubStatus('error');
            setSubMessage('Please enter a valid email address.');
            return;
        }

        setSubmitting(true);
        setSubStatus('idle');
        const attr = getAttributionPayload('/past-issues');
        try {
            await axios.post(`${API}/subscribers`, { email: cleanEmail, ...attr });
            setSubStatus('success');
            setIsSubscribed(true);
            localStorage.setItem('tnf_subscribed', 'true');
            setSubMessage("Done! This week's issue is on its way to your inbox.");
        } catch (err) {
            const msg = err.response?.data?.detail || 'Subscription failed. Please try again.';
            if (msg.toLowerCase().includes('already')) {
                setSubStatus('success');
                setIsSubscribed(true);
                localStorage.setItem('tnf_subscribed', 'true');
                setSubMessage("You're already subscribed! Future issues arrive every Monday.");
            } else {
                setSubStatus('error');
                setSubMessage(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#070B14] text-slate-100 flex flex-col justify-between selection:bg-amber-500/30 selection:text-amber-200">
            {/* Top Navigation */}
            <header className="border-b border-slate-800/80 bg-[#0B1120]/90 backdrop-blur-md sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group">
                        <img
                            src="/logo192.png"
                            alt="The Neural Feed"
                            className="w-9 h-9 rounded-lg object-cover border border-emerald-500/30 shadow-sm transition-transform group-hover:scale-105"
                        />
                        <div className="flex flex-col">
                            <span className="font-black tracking-tight text-base text-white group-hover:text-amber-400 transition-colors">
                                THE NEURAL FEED
                            </span>
                            <span className="text-[10px] font-mono tracking-wider text-slate-400 uppercase -mt-0.5">
                                Weekly Magazine Archive
                            </span>
                        </div>
                    </Link>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/subscribe"
                            className="hidden sm:inline-flex items-center text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                        >
                            Get Monday's issue →
                        </Link>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            className="text-slate-300 hover:text-white hover:bg-slate-800/60 rounded-full text-xs font-semibold"
                        >
                            Back to News <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 max-w-5xl mx-auto px-4 py-10 md:py-14 w-full">
                {/* Hero Header */}
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <Badge variant="outline" className="mb-4 py-1 px-3 border-amber-500/40 bg-amber-500/10 text-amber-300 rounded-full text-xs font-mono font-semibold tracking-wide">
                        <BookOpen className="w-3.5 h-3.5 mr-1.5 inline text-amber-400" />
                        THE NEURAL FEED WEEKLY
                    </Badge>
                    <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white leading-tight mb-4 font-['Chivo',sans-serif]">
                        Magazine <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-200">Past Issues.</span>
                    </h1>
                    <p className="text-base text-slate-300 leading-relaxed max-w-xl mx-auto">
                        Explore past editions of The Neural Feed Weekly. 5-minute magazines covering the week's biggest AI news, with a page or two of AI-made comics at the back.
                    </p>
                </div>

                {/* Inline Subscription Banner */}
                <Card className="border border-amber-500/30 bg-gradient-to-r from-[#0F172A] via-[#1E1B4B]/30 to-[#0F172A] shadow-xl rounded-2xl mb-12 overflow-hidden">
                    <CardContent className="p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-1.5 text-center md:text-left">
                            <span className="text-[10px] font-mono font-bold tracking-wider uppercase text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                                📬 FREE SUBSCRIPTION
                            </span>
                            <h2 className="text-lg sm:text-xl font-bold text-white">
                                The week's biggest AI news, free every Monday.
                            </h2>
                            <p className="text-xs text-slate-300 max-w-md">
                                The Neural Feed Weekly: 6–8 AI stories in a 5-minute read, plus a page of AI-made comics at the back. Free. Unsubscribe anytime.
                            </p>
                        </div>

                        <form onSubmit={handleSubscribe} className="w-full md:w-auto flex flex-col sm:flex-row gap-2 shrink-0">
                            <div className="relative min-w-[240px]">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    type="email"
                                    required
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-9 h-10 text-xs bg-slate-900 border-slate-700 text-white rounded-lg focus-visible:ring-amber-500"
                                />
                            </div>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="h-10 text-xs font-bold bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg px-4"
                            >
                                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Get Monday's issue"}
                            </Button>
                        </form>
                    </CardContent>
                    {subStatus === 'success' && (
                        <div className="bg-emerald-950/40 border-t border-emerald-800/60 px-6 py-2.5 text-emerald-300 text-xs flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                            <span>{subMessage}</span>
                        </div>
                    )}
                    {subStatus === 'error' && (
                        <div className="bg-red-950/40 border-t border-red-800/60 px-6 py-2.5 text-red-300 text-xs flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                            <span>{subMessage}</span>
                        </div>
                    )}
                </Card>

                {/* News Issues Grid (Lead Offer) */}
                <div className="mb-14">
                    <h2 className="text-sm font-mono font-bold tracking-widest text-slate-400 uppercase mb-4 flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-amber-400" /> The Neural Feed Weekly
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {newsIssues.map((issueItem, idx) => (
                            <Card 
                                key={issueItem.id || idx}
                                className="border border-slate-800 bg-[#0B1120] hover:border-amber-500/40 transition-all duration-200 rounded-2xl overflow-hidden flex flex-col group"
                            >
                                <div className="p-6 flex flex-col justify-between flex-1 space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 text-[10px] font-mono">
                                                ISSUE #{String(issueItem.issue || (idx + 1)).padStart(2, '0')}
                                            </Badge>
                                            <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                                                <Calendar className="w-3 h-3 text-slate-500" />
                                                {issueItem.period_label || "Weekly Edition"}
                                            </span>
                                        </div>
                                        <h3 className="text-lg font-bold text-white group-hover:text-amber-400 transition-colors leading-snug">
                                            <a href={`/magazine/${issueItem.id || `magazine-news-issue-${String(issueItem.issue).padStart(2, '0')}`}`}>
                                                {issueItem.title || `The Neural Feed Weekly #${issueItem.issue}`}
                                            </a>
                                        </h3>
                                        <p className="text-xs text-slate-300 leading-relaxed">
                                            {issueItem.description || "The week's biggest AI news, in 5 minutes. 6–8 AI stories, plus AI-made comics at the back."}
                                        </p>
                                    </div>

                                    <div className="pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-3">
                                        <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                            <FileText className="w-3.5 h-3.5 text-amber-400/80" />
                                            {issueItem.page_count || 10} Pages • PDF
                                        </span>
                                        {subscriberToken ? (
                                            <a
                                                href={`/magazines/${issueItem.pdf_filename}?token=${subscriberToken}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
                                            >
                                                <Download className="w-3.5 h-3.5" /> Read Issue
                                            </a>
                                        ) : isSubscribed ? (
                                            <div className="flex flex-col items-end gap-1">
                                                <a
                                                    href={`/magazine/${issueItem.id || `magazine-news-issue-${String(issueItem.issue).padStart(2, '0')}`}`}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-amber-300 text-xs font-semibold transition-colors border border-amber-500/30"
                                                >
                                                    <FileText className="w-3.5 h-3.5" /> Preview issue
                                                </a>
                                                <span className="text-[10px] text-slate-400">Full issue link in your Monday email</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <a
                                                    href={`/magazine/${issueItem.id || `magazine-news-issue-${String(issueItem.issue).padStart(2, '0')}`}`}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition-colors border border-slate-700"
                                                >
                                                    <FileText className="w-3.5 h-3.5" /> Preview
                                                </a>
                                                <Link
                                                    to="/subscribe"
                                                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition-all shadow-sm hover:shadow"
                                                >
                                                    <Sparkles className="w-3.5 h-3.5" /> Subscribe free
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>

                {/* Comics Archive Section (Secondary / Below) */}
                {toonIssues.length > 0 && (
                    <div className="mb-14 pt-8 border-t border-slate-800/80">
                        <div className="mb-4">
                            <h2 className="text-sm font-mono font-bold tracking-widest text-slate-400 uppercase flex items-center gap-2">
                                <Smile className="w-4 h-4 text-slate-400" /> Comics Archive (The Neural Feed Illustrated)
                            </h2>
                            <p className="text-xs text-slate-400 mt-1">
                                Previous issues of our visual satire and illustrated tech parodies.
                            </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {toonIssues.map((issueItem, idx) => (
                                <Card 
                                    key={issueItem.id || idx}
                                    className="border border-slate-800/80 bg-[#0B1120]/70 hover:border-slate-700 transition-all duration-200 rounded-2xl overflow-hidden flex flex-col group opacity-85 hover:opacity-100"
                                >
                                    <div className="p-6 flex flex-col justify-between flex-1 space-y-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <Badge className="bg-slate-800 text-slate-300 border-slate-700 text-[10px] font-mono">
                                                    TOON ISSUE #{String(issueItem.issue || (idx + 1)).padStart(2, '0')}
                                                </Badge>
                                                <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3 text-slate-500" />
                                                    {issueItem.period_label || "Weekly Edition"}
                                                </span>
                                            </div>
                                            <h3 className="text-base font-bold text-white group-hover:text-amber-400 transition-colors leading-snug">
                                                <a href={`/magazine/${issueItem.id || `magazine-toons-issue-${String(issueItem.issue).padStart(2, '0')}`}`}>
                                                    {issueItem.title}
                                                </a>
                                            </h3>
                                            <p className="text-xs text-slate-400 leading-relaxed">
                                                {issueItem.description || "11-page printable comic edition with retro newspaper satire."}
                                            </p>
                                        </div>

                                        <div className="pt-3 border-t border-slate-800/60 flex items-center justify-between">
                                            <span className="text-[11px] text-slate-400">
                                                {issueItem.page_count || 11} Pages • PDF
                                            </span>
                                            <a
                                                href={`/magazine/${issueItem.id || `magazine-toons-issue-${String(issueItem.issue).padStart(2, '0')}`}`}
                                                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-medium transition-colors border border-slate-700"
                                            >
                                                <FileText className="w-3 h-3" /> Preview
                                            </a>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="border-t border-slate-800/80 py-8 text-center text-xs text-slate-400 space-y-2">
                <p>© 2026 The Neural Feed. Independent AI news, every Monday.</p>
                <div className="flex items-center justify-center gap-4 text-slate-400">
                    <Link to="/" className="hover:text-slate-200 transition-colors">Daily News Feed</Link>
                    <span>•</span>
                    <Link to="/subscribe" className="hover:text-slate-200 transition-colors">Subscribe</Link>
                    <span>•</span>
                    <Link to="/privacy" className="hover:text-slate-200 transition-colors">Privacy Policy</Link>
                    <span>•</span>
                    <a href="mailto:contact@theneuralfeed.com" className="hover:text-slate-200 transition-colors">Contact</a>
                </div>
            </footer>
        </div>
    );
}
